import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

interface ExternalSharerFormData {
  fullName: string;
  dob: string;
  gender: string;
  phonNumber: string;
  email: string;
  qualification: string;
  specialization: string;
}

const ExternalSharer: React.FC = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [loading, setLoading] = useState(false);
  const [externalSharerData, setExternalSharerData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");

  const [formData, setFormData] = useState<ExternalSharerFormData>({
    fullName: "",
    dob: "",
    gender: "Male",
    phonNumber: "",
    email: "",
    qualification: "",
    specialization: "",
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/external-sharer/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: debouncedSearch,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setExternalSharerData(response.data?.data || []);
      setTotal(response.data?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching external sharers:", error);
      toast.error("Error fetching external sharers");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (id: string, rowData: any) => {
    setEditData(id);
    setFormData({
      fullName: rowData?.fullName || "",
      dob: rowData?.dob ? rowData.dob.split("T")[0] : "", // Format date for input
      gender: rowData?.gender || "Male",
      phonNumber: rowData?.phonNumber || "",
      email: rowData?.email || "",
      qualification: rowData?.qualification || "",
      specialization: rowData?.specialization || "",
    });
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  const handleSubmit = async () => {
    // Validate required fields
    if (
      !formData.fullName ||
      !formData.dob ||
      !formData.gender ||
      !formData.phonNumber
    ) {
      toast.error("Please fill all required fields (Name, DOB, Gender, Phone)");
      return;
    }

    // Validate phone number format
    const phoneRegex = /^[0-9]{11}$/;
    if (!phoneRegex.test(formData.phonNumber)) {
      toast.error("Phone number must be 11 digits");
      return;
    }

    // Validate email format if provided
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address");
        return;
      }
    }

    try {
      const payload = {
        ...formData,
        // Ensure optional fields are not empty strings
        email: formData.email || undefined,
        qualification: formData.qualification || undefined,
        specialization: formData.specialization || undefined,
      };

      if (editData) {
        const response = await axios.patch(
          `${process.env.REACT_APP_ADMIN_API_URL}/external-sharer/updateById/${editData}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(
            response?.data?.message || "Failed to update external sharer"
          );
        } else {
          toast.success(
            response?.data?.message || "External sharer updated successfully"
          );
          resetForm();
          fetchData();
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_API_URL}/external-sharer/registerExternalSharer`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(
            response?.data?.message || "Failed to register external sharer"
          );
        } else {
          toast.success(
            response?.data?.message || "External sharer registered successfully"
          );
          resetForm();
          fetchData();
        }
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || error.message || "Something went wrong"
      );
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      dob: "",
      gender: "Male",
      phonNumber: "",
      email: "",
      qualification: "",
      specialization: "",
    });
    setEditData(null);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_ADMIN_API_URL}/external-sharer/deleteById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(
        response.data.message || "External sharer deleted successfully"
      );
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || error.message || "Something went wrong"
      );
    }
    setIsDeleteModalOpen(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          External Sharer Registration
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            placeholder="Search by name or phone..."
            variant="outlined"
            value={searchInput}
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={5} md={5}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 2,
              boxShadow: 1,
            }}
          >
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  className="custom-input"
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  fullWidth
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  className="custom-input"
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  fullWidth
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ backgroundColor: "white" }}>
                    Gender *
                  </InputLabel>
                  <Select
                    size="small"
                    className="custom-input"
                    value={formData.gender}
                    name="gender"
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Phone Number "
                  name="phonNumber"
                  value={formData.phonNumber}
                  onChange={handleChange}
                  fullWidth
                  placeholder="03001234567"
                  inputProps={{ maxLength: 11 }}
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Email (Optional)"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  placeholder="example@email.com"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Qualification (Optional)"
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleChange}
                  fullWidth
                  placeholder="e.g., MBBS, MD, PhD"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Specialization (Optional)"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleChange}
                  fullWidth
                  placeholder="e.g., Cardiology, Neurology"
                />
              </Grid>
              <Grid item xs={6}>
                {/* Empty space for symmetry */}
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={
                !formData.fullName ||
                !formData.dob ||
                !formData.gender ||
                !formData.phonNumber
              }
            >
              {editData ? "Update External Sharer" : "Register External Sharer"}
            </Button>

            {editData && (
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={resetForm}
                sx={{ mt: 1 }}
              >
                Cancel Edit
              </Button>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={7} md={7}>
          <DynamicTable
            headers={[
              "SN",
              "Full Name",
              "DOB",
              "Gender",
              "Phone",
              "Email",
              "Qualification",
              "Specialization",
              "Actions",
            ]}
            data={externalSharerData?.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item?.fullName || "--",
              formatDate(item?.dob),
              item?.gender || "--",
              item?.phonNumber || "--",
              item?.email || "--",
              item?.qualification || "--",
              item?.specialization || "--",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: (
                  <Tooltip title="Edit External Sharer">
                    <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />
                  </Tooltip>
                ),
                onClick: (rowIndex: number) =>
                  handleEditClick(
                    externalSharerData[rowIndex]?._id,
                    externalSharerData[rowIndex]
                  ),
              },
              {
                icon: (
                  <Tooltip title="Delete External Sharer">
                    <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                  </Tooltip>
                ),
                onClick: (rowIndex: number) =>
                  handleDeleteClick(externalSharerData[rowIndex]?._id),
              },
            ]}
            height={"60vh"}
          />
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this external sharer? This action cannot be undone."
      />
    </Box>
  );
};

export default ExternalSharer;
